import React, { useState, useEffect } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  CssBaseline,
  Paper,
  Popper,
  MenuList,
  MenuItem,
  useMediaQuery,
  useTheme,
  Box,
  ClickAwayListener,
  Grow,
  Menu,
} from "@mui/material";
import { Menu as MenuIcon, ChevronRight } from "@mui/icons-material";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { alpha, styled } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import actionTypes from "../../../store/actions";

import {
  Billing,
  Customers,
  MyOfferings,
  Orders,
  Products,
  Reports,
  Settlement,
  Subscriptions,
  Contracts,
  Quotes,
  CollapseMenu,
  AssetIcon,
  Settings,
  SupportIcon,
  LeadIcon,
  DashboardIcon,
  ClearIcon,
} from "../../icons/icons";
import { DefaultLogoImage } from "../../../helper/setFallbackImages";

// 🧱 Constants
const drawerWidth = 240;
const collapsedDrawerWidth = 64;

// 🧩 Styled Drawer (replaces makeStyles drawer)
const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: open ? drawerWidth : collapsedDrawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  "& .MuiDrawer-paper": {
    width: open ? drawerWidth : collapsedDrawerWidth,
    padding: theme.spacing(1),
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
}));

const ResponsiveDrawer = (props) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedItem, setSelectedItem] = useState(currentPath);
  const [anchorEl, setAnchorEl] = useState(null);
  const [popperOpen, setPopperOpen] = useState(false);

  const themeColor = props.tenantData?.color || theme.palette.primary.main;

  useEffect(() => setSelectedItem(currentPath), [currentPath]);
  useEffect(() => {
    if (props.isMenuDrawerOpen === undefined) {
      props.setMenuDrawerOpen(!isMobile);
    }
  }, []);

  const handleDrawerToggle = () => {
    props.setMenuDrawerOpen(!props.isMenuDrawerOpen);
    setPopperOpen(false);
    setSelectedItem(currentPath);
  };

  const handleReportsClick = (event) => {
    setAnchorEl(event.currentTarget);
    setPopperOpen(!popperOpen);
  };

  const handleMenuItemClick = (route) => {
    setPopperOpen(false);
    if (isMobile) props.setMenuDrawerOpen(false);
    navigate(route);
    setSelectedItem(route);
  };

  const handleGenericReportsClick = (item) => {
    setPopperOpen(false);
    if (isMobile) props.setMenuDrawerOpen(false);
    const reportParams = {
      reportDescription: item.description,
      inputParameters: item.inputParameters || [],
      customFilter: item.customFilter || [],
    };
    navigate(`/report/${item.name}`, { state: reportParams });
    setSelectedItem("report");
  };

  const isSubMenuSelected = (subMenu) =>
    subMenu && subMenu.some((subItem) => selectedItem === subItem.route);

  const getLogo = () => {
    const { logo, favIcon, title } = props.tenantData || {};
    if (!logo && !favIcon)
      return (
        <img src="" onError={DefaultLogoImage} alt={props.t("ADMIN_TENANT")} />
      );
    let src = '';
    if (props.isMenuDrawerOpen) {
      src = logo ? logo : favIcon;
    } else {
      src = favIcon ? favIcon : logo;
    }
    // const extension = src.startsWith("data:image")
    //   ? ""
    //   : 
    //   src.toLowerCase().includes("svg")
    //     ? "svg+xml"
    //     : "png";
    var extension = "";
    if (logo.startsWith("data:image")) {
      extension = "";
    } else {
      let decoded = atob(decodeURIComponent(logo));
      var lowerCase = decoded.toLowerCase();
      if (lowerCase.indexOf("png") === 1) {
        extension = "png";
      } else if (
        lowerCase.indexOf("jpg") === 1 ||
        lowerCase.indexOf("jpeg") === 1
      ) {
        extension = "jpg";
      } else if (lowerCase.includes("svg")) {
        extension = "svg+xml";
      } else if (
        lowerCase.indexOf("tif") === 1 ||
        lowerCase.indexOf("tiff") === 1
      ) {
        extension = "tiff";
      }
    }

    return (
      <img
        onError={DefaultLogoImage}
        src={
          src.startsWith("data:image")
            ? src
            : `data:image/${extension};base64,${encodeURIComponent(src)}`
        }
        alt={title}
        style={{ maxWidth: "100%", maxHeight: "50px", objectFit: "contain" }}
      />
    );
  };

  const menuItems = [
    (props.providerType === "SALES_CHNL_PROVIDER" ||
      props.providerType === "SOLUTION_PROVIDER") &&
    props.menuPaths.includes("/dashboard") &&
    !props.securityGroupsList.includes("PARTNER_EMPLOYEE") && {
      text: props.t("DASHBOARD"),
      icon: <DashboardIcon />,
      route: "/dashboard",
    },

    (props.userPerms.includes("CATALOG_ADMIN_ALL") ||
      props.userPerms.includes("CATALOG_CREATE") ||
      props.userPerms.includes("CATALOG_ADMIN") ||
      props.userPerms.includes("CATALOG_VIEW")) &&
    props.menuPaths.includes("/myOfferings") && {
      text: props.t("SOLUTIONS"),
      icon: <MyOfferings />,
      route: "/myOfferings",
    },

    props.userPerms.includes("CUMULATOR-PRODUCT_VIEW") &&
    !props.menuPaths.includes("/myOfferings") && {
      text: props.t("SOLUTIONS"),
      icon: <MyOfferings />,
      route: "/newOrder",
    },

    (props.loggedInUserType !== "ECOMMERCE_CUSTOMER" ||
      props.loggedInUserType !== "ECOMMERCE_ADMIN" ||
      props.loggedInUserType !== "SALES_REPRESENTATIVE") &&
    (props.userPerms.includes("CATALOG_ADMIN_ALL") ||
      props.userPerms.includes("CATALOG_CREATE") ||
      props.userPerms.includes("CATALOG_ADMIN") ||
      props.userPerms.includes("CATALOG_VIEW")) &&
    props.menuPaths.includes("/products") && {
      text: props.t("PRODUCTS"),
      icon: <Products />,
      route: "/products",
    },

    props.menuPaths.includes("/manageLeads") &&
    props.loggedInUserType !== "ECOMMERCE_CUSTOMER" &&
    props.loggedInUserType !== "ECOMMERCE_ADMIN" && {
      text: props.t("LEADS"),
      icon: <LeadIcon />,
      route: "/manageLeads",
    },

    props.menuPaths.includes("/manageCustomers") && {
      text:
        props.loggedInUserType === "ECOMMERCE_CUSTOMER" ||
          props.loggedInUserType === "ECOMMERCE_ADMIN"
          ? props.t("ORGANIZATION")
          : props.t("CUSTOMERS"),
      icon: <Customers />,
      route: "/manageCustomers",
    },

    props.providerType === "SALES_CHNL_PROVIDER" &&
    props.menuPaths.includes("/orders") &&
    props.quoteEnabled && {
      text: props.t("QUOTES"),
      icon: <Quotes />,
      route: "/quotes",
    },

    props.menuPaths.includes("/orders") && {
      text:
        props.providerType === "SALES_CHNL_PROVIDER"
          ? props.t("ORDERS")
          : props.t("FULFILMENT_REQUEST"),
      icon: <Orders />,
      route: "/orders",
    },

    props.menuPaths.includes("/contracts") && {
      text: props.t("CONTRACTS"),
      icon: <Contracts />,
      route: "/contracts",
    },

    props.menuPaths.includes("/subscriptions") && {
      text: props.t("SUBSCRIPTIONS"),
      icon: <Subscriptions />,
      route: "/subscriptions",
    },

    props.menuPaths.includes("/subscriptions") && {
      text: props.t("ASSETS"),
      icon: <AssetIcon />,
      route: "/assets",
    },

    props.menuPaths.includes("/billing") && {
      text: props.t("BILLING"),
      icon: <Billing />,
      route: "/billing",
    },

    props.menuPaths.includes("/settlement") &&
    props.loggedInUserType !== "ECOMMERCE_CUSTOMER" &&
    props.loggedInUserType !== "ECOMMERCE_ADMIN" && {
      text: props.t("SETTLEMENTS"),
      icon: <Settlement />,
      route: "/settlement",
    },

    props.userPerms.includes("CUMULATOR-REPORT_VIEW") &&
    props.loggedInUserType !== "ECOMMERCE_CUSTOMER" &&
    props.loggedInUserType !== "ECOMMERCE_ADMIN" && {
      text: props.t("REPORTS"),
      icon: <Reports />,
      onClick: (e) => {
        handleReportsClick(e);
        setSelectedItem("/reports");
      },
      subMenu: [
        props.menuPaths.includes("/orderReport") && {
          text: props.t("SALES_ORDER"),
          route: "/orderReport",
        },
        props.menuPaths.includes("/billingRecords") && {
          text: props.t("BILLING_REPORTS"),
          route: "/billingRecords",
        },
        props.menuPaths.includes("/inventoryReport") &&
        { text: props.t("INVENTORY"), route: "/inventoryReport" },
        props.menuPaths.includes("/paymentReport") &&
        { text: props.t("PAYMENT_RECEIPT"), route: "/paymentReport" },
        props.menuPaths.includes("/workOrders") &&
        { text: props.t("WORK_ORDERS"), route: "/workOrders" },
        props.menuPaths.includes("/stats") &&
        { text: props.t("STATS"), route: "/stats" }
      ],
      route: "/reports",
    },
  ];

  const drawer = (
    <div>
      <div
        style={{
          overflow: "hidden",
          height: "46px",
          display: "flex",
          alignItems: "center",
          justifyContent: props.isMenuDrawerOpen ? "space-between" : "center",
        }}
      >
        {!isMobile ? (
          <a href="/">{getLogo()}</a>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <a href="/">{getLogo()}</a>
            <IconButton onClick={handleDrawerToggle}>
              <ClearIcon fontSize="large" />
            </IconButton>
          </div>
        )}
      </div>
      <Divider />

      {/* 👇 KEEPING YOUR FULL MENU LOGIC INTACT */}
      <List className="left-menu-wrapper">
        {menuItems.filter(item => item !== undefined).filter(Boolean)
          .map((item, index) => {
            const isSelected =
              selectedItem === item.route || isSubMenuSelected(item.subMenu);
            return (
              <React.Fragment key={index}>
                <ListItem
                  button
                  onClick={
                    item.text === props.t("REPORTS")
                      ? item.onClick
                      : () => {
                        setSelectedItem(item.route);
                        if (isMobile) props.setMenuDrawerOpen(false);
                        setPopperOpen(false);
                        navigate(item.route);
                      }
                  }
                  sx={{
                    borderRadius: 1,
                    backgroundColor: isSelected ? themeColor : "transparent",
                    color: isSelected ? "#fff" : "inherit",
                    "&:hover": {
                      backgroundColor: isSelected ? themeColor : alpha(themeColor, 0.12),
                      color: themeColor,
                    },
                  }}
                >
                  {!props.isMenuDrawerOpen ? (
                    <Tooltip title={item.text}>
                      <ListItemIcon
                        sx={{
                          minWidth: "2rem",
                          color: isSelected ? "#fff" : "inherit",
                          "& svg": {
                            // fontSize: '1.5rem',
                            marginTop: '8px',
                            marginBottom: '8px',
                          }
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                    </Tooltip>
                  ) : (
                    <ListItemIcon
                      sx={{
                        minWidth: "2rem",
                        color: isSelected ? "#fff" : "inherit",
                        "& svg": {
                          // fontSize: '1.5rem',
                        }
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                  )}
                  {props.isMenuDrawerOpen && (
                    <ListItemText
                      primary={item.text}
                      sx={{ color: isSelected ? "#fff" : "inherit" }}
                    />
                  )}
                </ListItem>
              </React.Fragment>
            );
          })}
        {/* operation portal */}
        {/* {props.providerType === "SALES_CHNL_PROVIDER" && props.userPerms.includes("OFBTOOLS_VIEW") &&
                  <ListItem button classes={{ root: classes.listItem }}
                    onClick={(e) => {
                      props.openOperPortal(null, null);
                    }}
                    style={{
                      // backgroundColor: ((selectedItem === item.route) || isSubMenuSelected(item.subMenu)) ? props.tenantData.color : 'transparent', // theme bg for selected item
                      // color: ((selectedItem === item.route) || isSubMenuSelected(item.subMenu)) ? '#fff' : 'inherit', // White text if selected
                      // borderRadius: '6px'
                    }}>
                    {!props.isMenuDrawerOpen ?
                      <Tooltip title={props.t('OPER_PORTAL')}>
                        <ListItemIcon style={{ minWidth: '2rem', }}><Settings /></ListItemIcon>
                      </Tooltip>
                      :
                      <ListItemIcon style={{ minWidth: '2rem', }}><Settings /></ListItemIcon>
                    }
                    {props.isMenuDrawerOpen && <ListItemText style={{}} primary={props.t('OPER_PORTAL')} />}
                  </ListItem>
                } */}
        {props.providerType === "SALES_CHNL_PROVIDER" && props.userPerms.includes("OFBTOOLS_VIEW") &&
          <ListItem
            button
            onClick={(e) => {
              props.openOperPortal(null, null);
            }}
            sx={{
              borderRadius: 1,
              backgroundColor: "transparent",
              color: "inherit",
              "&:hover": {
                backgroundColor: alpha(themeColor, 0.12),
                color: themeColor,
              },
              marginBottom: '8px',
            }}
          >
            {!props.isMenuDrawerOpen ? (
              <Tooltip title={props.t('OPER_PORTAL')}>
                <ListItemIcon
                  sx={{
                    minWidth: "2rem",
                    color: "inherit",
                  }}
                >
                  <Settings />
                </ListItemIcon>
              </Tooltip>
            ) : (
              <ListItemIcon
                sx={{
                  minWidth: "2rem",
                  color: "inherit",
                }}
              >
                <Settings />
              </ListItemIcon>
            )}
            {props.isMenuDrawerOpen && (
              <ListItemText
                primary={props.t('OPER_PORTAL')}
                sx={{ color: "inherit" }}
              />
            )}
          </ListItem>
        }
        <Divider />
        {/* ticketing portal */}
        <ListItem
          button
          onClick={(e) => {
            window.open('https://digitizethings-sm.atlassian.net/servicedesk/customer/portals', '_blank');
          }}
          sx={{
            borderRadius: 1,
            backgroundColor: "transparent",
            color: "inherit",
            "&:hover": {
              backgroundColor: alpha(themeColor, 0.12),
              color: themeColor,
            },
            marginTop: '8px',
          }}
        >
          {!props.isMenuDrawerOpen ? (
            <Tooltip title={props.t('TICKET')}>
              <ListItemIcon
                sx={{
                  minWidth: "2rem",
                  color: "inherit",
                }}
              >
                <SupportIcon />
              </ListItemIcon>
            </Tooltip>
          ) : (
            <ListItemIcon
              sx={{
                minWidth: "2rem",
                color: "inherit",
              }}
            >
              <SupportIcon />
            </ListItemIcon>
          )}
          {props.isMenuDrawerOpen && (
            <ListItemText
              primary={props.t('TICKET')}
              sx={{ color: "inherit" }}
            />
          )}
        </ListItem>
      </List>

      <Divider />
      <Box sx={{ mt: 1 }}>
        <IconButton onClick={handleDrawerToggle}>
          {props.isMenuDrawerOpen ? (
            <>
              <CollapseMenu />
              <span style={{ fontSize: "14px", marginLeft: ".5rem" }}>
                Collapse Menu
              </span>
            </>
          ) : (
            <ChevronRight />
          )}
        </IconButton>
      </Box>
    </div>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      {isMobile && (
        <IconButton
          onClick={handleDrawerToggle}
          sx={{ position: "absolute", left: 0 }}
        >
          <MenuIcon />
        </IconButton>
      )}
      <StyledDrawer
        variant="permanent"
        open={props.isMenuDrawerOpen}
        sx={{
          display: isMobile
            ? props.isMenuDrawerOpen
              ? "block"
              : "none"
            : "block",
        }}
      >
        {drawer}
      </StyledDrawer>

      {/* Reports Popper */}
      <Popper
        open={popperOpen}
        anchorEl={anchorEl}
        sx={{ zIndex: 1300 }}
        role={undefined}
        placement="right-start"
        transition
        disablePortal
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [8, 0],
            },
          },
          {
            name: 'preventOverflow',
            options: {
              altBoundary: true,  // Prevent overflow
              padding: 8,  // 8px padding from boundaries
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: 'left top'
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={() => setPopperOpen(false)}>

                <MenuList sx={{ padding: 0 }}>
                  {menuItems.find(item => item.route === selectedItem)?.subMenu
                    ?.filter(Boolean)
                    .map((subItem, subIndex) => (
                      <MenuItem
                        key={subIndex}
                        onClick={() => handleMenuItemClick(subItem.route)}
                      >
                        {subItem.text}
                      </MenuItem>
                    ))}


                  {/* generic reports */}
                  {props.loggedInUserType === "ECOMMERCE_CUSTOMER" || props.loggedInUserType === "ECOMMERCE_ADMIN" ?
                    <>
                      {props.genericReportsList && props.genericReportsList.length > 0 && props.genericReportsList.map((grReport, grIndex) => (
                        (grReport.name === 'getItemsShippedToCustomer' || grReport.name === 'getInventoryReceiptReport') &&
                        <MenuItem
                          key={grIndex}
                          onClick={() => handleGenericReportsClick(grReport)}
                        >
                          {grReport.description}
                        </MenuItem>
                      ))
                      }
                    </>
                    :
                    <>
                      {props.genericReportsList && props.genericReportsList.length > 0 && props.genericReportsList.map((grReport, grIndex) => (
                        grReport.name !== 'getMonthlyIFRSReport' && grReport.name !== 'getSolutionProviderSettlements' &&
                        <MenuItem
                          key={grIndex}
                          onClick={() => handleGenericReportsClick(grReport)}
                        >
                          {grReport.description}
                        </MenuItem>
                      ))
                      }
                      {props.genericReportsList && props.genericReportsList.length > 0 && props.genericReportsList.some(item => item.name === 'getMonthlyIFRSReport') &&
                        <MenuItem
                          onClick={() => handleMenuItemClick('/ifrsReport')}
                        >
                          {props.t('IFRS_REPORT')}
                        </MenuItem>
                      }
                    </>
                  }
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  tenantData: state.auth.tenantData,
  isMenuDrawerOpen: state.auth.isMenuDrawerOpen,
  providerType: state.auth.providerType,
  securityGroupsList: state.auth.securityGroupsList,
});

const mapDispatchToProps = (dispatch) => ({
  setMenuDrawerOpen: (data) =>
    dispatch({ type: actionTypes.SET_MENU_DRAWER_OPEN, data }),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation("translations")(ResponsiveDrawer));
